import { getQueryClient } from '@/lib/query/client/query-client';
import { useQuery } from '@tanstack/react-query';

import { shelvesApi } from './shelves.api';
import type { ShelfContainersListParams, ShelvesListParams } from '../types';
import { STALE_TIME } from '@/lib/query/stale-times';

export const shelfKeys = {
  all: ['shelves'] as const,
  lists: () => [...shelfKeys.all, 'list'] as const,
  list: (filters?: ShelvesListParams) =>
    [...shelfKeys.lists(), filters] as const,
  details: () => [...shelfKeys.all, 'detail'] as const,
  detail: (id: number) => [...shelfKeys.details(), id] as const,
  stats: () => [...shelfKeys.all, 'stats'] as const,
  containers: (id: number, params?: ShelfContainersListParams) =>
    [...shelfKeys.detail(id), 'containers', params] as const
};

export const shelfQueries = {
  list: (filters?: ShelvesListParams) => ({
    queryKey: shelfKeys.list(filters),
    queryFn: () => shelvesApi.getAll(filters)
  }),

  detail: (id: number) => ({
    queryKey: shelfKeys.detail(id),
    queryFn: () => shelvesApi.getById(id),
    enabled: id > 0
  }),

  stats: () => ({
    queryKey: shelfKeys.stats(),
    queryFn: () => shelvesApi.getStats(),
    // `stats`, not `metrics`: ShelfService caches this under
    // `cache_ttl.stats` (120s), so a 60s refetch just re-reads the same payload.
    staleTime: STALE_TIME.stats
  }),

  containers: (id: number, params?: ShelfContainersListParams) => ({
    queryKey: shelfKeys.containers(id, params),
    queryFn: () => shelvesApi.getContainers(id, params),
    enabled: id > 0
  })
};

export const useShelves = (filters?: ShelvesListParams) => {
  return useQuery({
    ...shelfQueries.list(filters),
    placeholderData: (previousData) => previousData
  });
};

export const useShelf = (id: number) => {
  return useQuery(shelfQueries.detail(id));
};

export const useShelfContainers = (
  id: number,
  params?: ShelfContainersListParams,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    ...shelfQueries.containers(id, params),
    enabled: id > 0 && (options?.enabled ?? true),
    placeholderData: (previousData) => previousData
  });
};

export const useShelfStats = () => {
  return useQuery(shelfQueries.stats());
};

export const prefetchShelves = (filters?: ShelvesListParams) => {
  return getQueryClient().prefetchQuery(shelfQueries.list(filters));
};
